import React from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { ActivityAssociation, Activity } from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import styles from './createActivityModal.module.scss';
import './muiTimeCalendar.scss';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import CreateActivityForm, { CreateActivityFields } from './CreateActivityForm';
import { DATE_FORMAT } from 'utils/constants';
import { FormProvider, useForm } from 'react-hook-form';

interface CreateActivityModalProps {
  activityToEdit?: Activity;
  defaultAssociations?: ActivityAssociation[];
  onClose: () => void;
}

const CreateActivityModal = ({
  activityToEdit,
  defaultAssociations = [],
  onClose,
}: CreateActivityModalProps) => {
  const formContext = useForm<CreateActivityFields>({
    mode: 'onChange',
    defaultValues: {
      description: activityToEdit?.description || '',
      relationships: activityToEdit
        ? activityToEdit.relationships
        : defaultAssociations,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = formContext;

  const closeModal = () => {
    reset({
      description: '',
      type: { label: '', value: '' },
    });
    onClose();
  };

  const { isLoading: isActivityLoading, mutate: saveActivityMutation } =
    useMutation({
      mutationFn: async (data: CreateActivityFields) => {
        const cleanedData = {
          ...data,
          relationships: data.relationships.filter(
            (rel) => rel.selected !== false
          ),
          timestamp: data.timestamp?.format(DATE_FORMAT.STANDARD_TIME),
          type: data.type?.value,
        };
        const restFunction = activityToEdit
          ? restFactory.put
          : restFactory.post;
        const requestURL = activityToEdit
          ? `/dj/api/v1/activity/${activityToEdit?.id}/`
          : `/dj/api/v1/activity/`;
        await restFunction(requestURL, cleanedData);
      },
      onSuccess: () => {
        closeModal();
        toast.success(
          activityToEdit ? 'Activity saved.' : 'New activity saved.'
        );
      },
      onError: () => {
        toast.error('Error saving activity.');
      },
    });

  const onSubmit = async (data: CreateActivityFields) => {
    saveActivityMutation(data);
  };

  return (
    <ConfirmationModal
      className={styles.viewModals}
      centered={true}
      animation={false}
      show={true}
      onCancel={() => closeModal()}
      okBtnText="Save"
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      title={activityToEdit ? 'Edit Action' : 'Add a New Activity'}
      isLoading={isActivityLoading}
      isDraggable
    >
      <FormProvider {...formContext}>
        <CreateActivityForm
          activityToEdit={activityToEdit}
          defaultAssociations={defaultAssociations}
        />
      </FormProvider>
    </ConfirmationModal>
  );
};

export default CreateActivityModal;
